import { alpha, createTheme } from '@mui/material/styles';

export const brandTokens = {
  primaryBlue: '#2563EB',
  darkBlue: '#0D47A1',
  deepNavy: '#0A0F1C',
  accentBlue: '#00B2FF',
  lightGray: '#F3F5F9',
  surface: '#FAFAFA',
  white: '#FFFFFF',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  border: '#D1D5DB',
  successGreen: '#22C55E',
  warningAmber: '#F59E0B',
  errorRed: '#EF4444',
};

const uiRadii = {
  base: 8,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 14,
  dialog: 16,
  pill: 999,
} as const;

const baseTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1760,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: brandTokens.primaryBlue,
      dark: brandTokens.darkBlue,
      light: '#6EA8FF',
      contrastText: brandTokens.white,
    },
    secondary: {
      main: brandTokens.deepNavy,
      dark: '#050915',
      light: '#1F2A44',
      contrastText: brandTokens.white,
    },
    success: {
      main: brandTokens.successGreen,
      dark: '#169B46',
      light: '#6EE7A0',
      contrastText: brandTokens.white,
    },
    info: {
      main: brandTokens.accentBlue,
      dark: '#0086C3',
      light: '#7DDCFF',
      contrastText: brandTokens.white,
    },
    warning: {
      main: brandTokens.warningAmber,
      dark: '#D97706',
      light: '#FBCB73',
      contrastText: brandTokens.white,
    },
    error: {
      main: brandTokens.errorRed,
      dark: '#DC2626',
      light: '#FCA5A5',
      contrastText: brandTokens.white,
    },
    text: {
      primary: brandTokens.textPrimary,
      secondary: brandTokens.textSecondary,
    },
    background: {
      default: '#EEF4FF',
      paper: brandTokens.white,
    },
    divider: alpha(brandTokens.deepNavy, 0.08),
  },
  shape: {
    borderRadius: uiRadii.base,
  },
  typography: {
    fontFamily: [
      'Inter',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '3rem',
      fontWeight: 800,
      letterSpacing: '-0.04em',
    },
    h2: {
      fontSize: '2.375rem',
      fontWeight: 800,
      letterSpacing: '-0.035em',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 700,
      letterSpacing: '-0.03em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontSize: '1.05rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    subtitle1: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    subtitle2: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
      letterSpacing: '-0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.55,
      letterSpacing: '-0.01em',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
      letterSpacing: '-0.01em',
    },
    caption: {
      letterSpacing: '0.01em',
    },
  },
});

const shadows = [...baseTheme.shadows] as typeof baseTheme.shadows;
shadows[1] = '0 8px 24px rgba(15, 23, 42, 0.06)';
shadows[2] = '0 14px 36px rgba(15, 23, 42, 0.08)';
shadows[3] = '0 18px 44px rgba(15, 23, 42, 0.10)';
shadows[4] = '0 22px 52px rgba(15, 23, 42, 0.12)';
shadows[6] = '0 28px 60px rgba(15, 23, 42, 0.14)';

const containedGradients = {
  primary: `linear-gradient(135deg, ${brandTokens.primaryBlue} 0%, ${brandTokens.darkBlue} 100%)`,
  secondary: `linear-gradient(135deg, ${brandTokens.deepNavy} 0%, #182B57 100%)`,
  success: `linear-gradient(135deg, ${brandTokens.successGreen} 0%, #169B46 100%)`,
  info: `linear-gradient(135deg, ${brandTokens.accentBlue} 0%, #0086C3 100%)`,
  warning: `linear-gradient(135deg, ${brandTokens.warningAmber} 0%, #D97706 100%)`,
  error: `linear-gradient(135deg, ${brandTokens.errorRed} 0%, #DC2626 100%)`,
} as const;

export const theme = createTheme(baseTheme, {
  shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: 'light',
        },
        html: {
          backgroundColor: brandTokens.lightGray,
        },
        body: {
          backgroundColor: '#EEF4FF',
          backgroundImage: `
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 28%),
            radial-gradient(circle at top right, rgba(0, 178, 255, 0.10), transparent 24%),
            linear-gradient(180deg, #F8FBFF 0%, #EEF4FF 38%, #F3F5F9 100%)
          `,
          color: brandTokens.textPrimary,
        },
        '#root': {
          minHeight: '100%',
        },
        '::selection': {
          backgroundColor: alpha(brandTokens.primaryBlue, 0.18),
          color: brandTokens.textPrimary,
        },
        a: {
          color: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }: any) => {
          const paletteColor =
            ownerState.color && ownerState.color !== 'inherit'
              ? ownerState.color
              : 'primary';
          const gradient =
            containedGradients[paletteColor as keyof typeof containedGradients] ??
            containedGradients.primary;

          return {
            borderRadius: uiRadii.md,
            padding: theme.spacing(1.125, 2),
            fontWeight: 700,
            lineHeight: 1.2,
            boxShadow: 'none',
            transition: theme.transitions.create(
              ['background-color', 'border-color', 'box-shadow', 'transform'],
              {
                duration: theme.transitions.duration.shorter,
              }
            ),
            ...(ownerState.size === 'small'
              ? {
                  minHeight: 34,
                  padding: theme.spacing(0.75, 1.5),
                }
              : {}),
            ...(ownerState.variant === 'contained'
              ? {
                  background: gradient,
                  color: theme.palette[paletteColor].contrastText,
                  boxShadow: '0 12px 28px rgba(37, 99, 235, 0.20)',
                  '&:hover': {
                    background: gradient,
                    boxShadow: '0 16px 32px rgba(37, 99, 235, 0.26)',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-disabled': {
                    background: alpha(theme.palette.text.primary, 0.08),
                    color: alpha(theme.palette.text.primary, 0.34),
                    boxShadow: 'none',
                  },
                }
              : {}),
            ...(ownerState.variant === 'outlined'
              ? {
                  borderWidth: 1,
                  borderColor: alpha(theme.palette[paletteColor].main, 0.26),
                  backgroundColor: alpha(theme.palette[paletteColor].main, 0.02),
                  '&:hover': {
                    borderColor: alpha(theme.palette[paletteColor].main, 0.36),
                    backgroundColor: alpha(theme.palette[paletteColor].main, 0.08),
                  },
                }
              : {}),
            ...(ownerState.variant === 'text'
              ? {
                  '&:hover': {
                    backgroundColor: alpha(theme.palette[paletteColor].main, 0.08),
                  },
                }
              : {}),
          };
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: uiRadii.md,
          transition: theme.transitions.create(['background-color', 'transform'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          backgroundImage: 'none',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.06)}`,
        }),
        rounded: {
          borderRadius: uiRadii.xl,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: uiRadii.xl,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }: any) => ({
          height: 3,
          borderRadius: uiRadii.pill,
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: uiRadii.md,
          backgroundColor: alpha(theme.palette.common.white, 0.92),
          '& fieldset': {
            borderColor: alpha(theme.palette.secondary.main, 0.14),
          },
          '&:hover fieldset': {
            borderColor: alpha(theme.palette.primary.main, 0.24),
          },
          '&.Mui-focused fieldset': {
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
        }),
        input: {
          paddingTop: 12,
          paddingBottom: 12,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '&::before': {
            borderBottomColor: alpha(theme.palette.secondary.main, 0.14),
          },
          '&:hover:not(.Mui-disabled, .Mui-error)::before': {
            borderBottomColor: alpha(theme.palette.primary.main, 0.26),
          },
          '&::after': {
            borderBottomColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ theme }: any) => ({
          color: alpha(theme.palette.text.primary, 0.62),
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          borderRadius: uiRadii.lg,
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
          boxShadow: theme.shadows[3],
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: uiRadii.sm,
          margin: theme.spacing(0.375, 0.75),
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.10),
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: uiRadii.sm,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          borderColor: alpha(theme.palette.secondary.main, 0.06),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: uiRadii.lg,
          alignItems: 'center',
        },
        standardSuccess: {
          backgroundColor: '#EEFDF3',
          color: '#166534',
        },
        standardInfo: {
          backgroundColor: '#EEF6FF',
          color: '#1D4ED8',
        },
        standardWarning: {
          backgroundColor: '#FFF7E8',
          color: '#B45309',
        },
        standardError: {
          backgroundColor: '#FFF1F2',
          color: '#B91C1C',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          borderRadius: uiRadii.dialog,
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
          boxShadow: theme.shadows[6],
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          padding: theme.spacing(2, 2.5, 2.5),
          gap: theme.spacing(1),
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: uiRadii.xl,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderBottomColor: alpha(theme.palette.secondary.main, 0.06),
        }),
        head: ({ theme }: any) => ({
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          color: theme.palette.text.secondary,
          fontWeight: 700,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: any) => ({
          backgroundColor: theme.palette.secondary.main,
          borderRadius: uiRadii.md,
          padding: theme.spacing(1, 1.25),
          boxShadow: theme.shadows[2],
        }),
        arrow: ({ theme }: any) => ({
          color: theme.palette.secondary.main,
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: uiRadii.pill,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          overflow: 'hidden',
        }),
        bar: {
          borderRadius: uiRadii.pill,
        },
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        thickness: 4,
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ theme }: any) => ({
          border: `2px solid ${theme.palette.background.paper}`,
          fontWeight: 700,
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: uiRadii.md,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderColor: alpha(theme.palette.secondary.main, 0.08),
        }),
      },
    },
  },
});
